import { CSSProperties } from 'react';
import { SxProps, Theme } from '@mui/material';

export const wrapperSx = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  overflow: 'hidden',
  marginBottom: '24px',
} as const;

const baseTextFieldSx = {
  height: 56,
  backgroundColor: 'var(--color-element)',
  borderRadius: '8px',
  fontSize: 14,
  color: 'var(--color-type-primary)',
  padding: '16px',
  border: '1px solid transparent',
  fontFamily: '"Manrope", sans-serif',
  '& fieldset': {
    display: 'none',
  },
  '&.Mui-disabled': {
    opacity: 0.7,
    color: 'var(--color-element-inactive)',
  },
  '&.Mui-error': {
    borderColor: 'var(--color-red)',
  },
};

export const getTextFieldWithIconsSx = (
  startIcon?: boolean,
  endIcon?: boolean,
  showIncrementDecrement?: boolean,
  readOnly?: boolean,
  clickable?: boolean,
  reverse?: boolean,
  labelSx?: SxProps<Theme>,
): SxProps<Theme> => {
  const labelStyles = labelSx ? (typeof labelSx === 'object' ? labelSx : {}) : {};

  return {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      ...baseTextFieldSx,
      ...(reverse && {
        backgroundColor: 'var(--color-background)',
      }),
      ...(startIcon && {
        paddingLeft: '48px',
      }),
      ...(endIcon || showIncrementDecrement
        ? {
            paddingRight: showIncrementDecrement ? '88px' : '48px',
          }
        : {}),
      ...(readOnly && {
        cursor: 'default',
      }),
      ...(clickable && {
        cursor: 'pointer',
      }),
    },
    '& .MuiOutlinedInput-input': {
      padding: 0,
      height: '24px',
      lineHeight: 1,
      fontWeight: 600,
      ...(readOnly && {
        cursor: 'default',
      }),
      ...(clickable && {
        cursor: 'pointer',
      }),
      '&::placeholder': {
        color: 'var(--color-type-secondary)',
        opacity: 0.9,
      },
      '&:-webkit-autofill': {
        WebkitBoxShadow: 'inset 0 0 0 1000px var(--color-element)',
        WebkitTextFillColor: 'var(--color-type-primary)',
      },
      '&:-webkit-autofill:hover': {
        WebkitBoxShadow: 'inset 0 0 0 1000px var(--color-element)',
        WebkitTextFillColor: 'var(--color-type-primary)',
      },
      '&:-webkit-autofill:focus': {
        WebkitBoxShadow: 'inset 0 0 0 1000px var(--color-element)',
        WebkitTextFillColor: 'var(--color-type-primary)',
      },
      '&.Mui-disabled': {
        WebkitTextFillColor: 'var(--color-element-inactive)',
      },
    },
    '& .MuiFormLabel-root': {
      fontSize: 12,
      fontWeight: 500,
      lineHeight: '16px',
      color: 'var(--color-type-secondary)',
      marginBottom: 1,
      transform: 'none',
      position: 'relative',
      fontFamily: '"Manrope", sans-serif',
      whiteSpace: 'normal',
      ...(labelStyles as CSSProperties),
      '&.Mui-focused': {
        color: 'var(--color-type-secondary)',
      },
      '&.Mui-disabled': {
        color: 'var(--color-type-secondary)',
      },
    },
    '& .MuiFormHelperText-root': {
      color: 'var(--color-red)',
      fontSize: 14,
      marginTop: 0.5,
      marginLeft: 0,
    },
  };
};

export const startAdornmentStyle = {
  position: 'absolute' as const,
  left: 16,
  top: '50%',
  transform: 'translateY(-50%)',
  display: 'flex',
  alignItems: 'center',
  pointerEvents: 'none' as const,
};

export const endAdornmentStyle = {
  position: 'absolute' as const,
  right: 16,
  top: '50%',
  transform: 'translateY(-50%)',
  display: 'flex',
  alignItems: 'center',
  gap: 4,
};

export const incrementDecrementContainerStyle = {
  display: 'flex' as const,
  flexDirection: 'column' as const,
  gap: 1,
};

export const getButtonStyle = (disabled?: boolean) => ({
  background: 'transparent',
  border: 'none',
  padding: 2,
  cursor: disabled ? ('default' as const) : ('pointer' as const),
  display: 'flex' as const,
  alignItems: 'center' as const,
  justifyContent: 'center' as const,
  opacity: disabled ? 0.5 : 1,
});

export const getClearButtonStyle = (disabled?: boolean) => ({
  background: 'transparent',
  border: 'none',
  padding: 0,
  cursor: disabled ? ('default' as const) : ('pointer' as const),
  display: 'flex' as const,
  alignItems: 'center' as const,
  opacity: disabled ? 0.5 : 1,
});
